import thanxLogoImage from 'assets/images/campaign-center/ic_thanx_push_mock.png'
import androidStatusBar from 'assets/images/campaign-center/push_android_statusbar.svg'
import androidTime from 'assets/images/campaign-center/push_android_time.svg'
import iosDate from 'assets/images/campaign-center/push_ios_date.svg'
import iosStatusBar from 'assets/images/campaign-center/push_ios_statusbar.svg'
import iosTime from 'assets/images/campaign-center/push_ios_time.svg'
import wallpaper from 'assets/images/campaign-center/push_preview_wallpaper.jpg'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import React, { useMemo, useState } from 'react'
import { Button, ButtonGroup } from 'react-bootstrap'
import AndroidPushPreview from './AndroidPushPreview'
import IosPushPreview from './IosPushPreview'

const t = buildTranslate('thanx_campaigns.builder.previews.push')

type Platform = 'ios' | 'android'

type Props = {
  text: string | null
  title?: string | null
  subtitle?: string | null
  imageUrl?: string | null
}

const PushRedesigned = ({ text, title, subtitle, imageUrl }: Props) => {
  const campaignMerchant = useCurrentMerchant()
  const [platform, setPlatform] = useState<Platform>('ios')
  const [isExpanded, setIsExpanded] = useState(false)

  const pushText = useMemo(() => {
    if (!text) {
      return ''
    }

    if (!campaignMerchant || campaignMerchant.branded_app) {
      return text
    }

    return t('custom', {
      merchant: campaignMerchant.nickname,
      customText: text,
    })
  }, [text, campaignMerchant])

  // Branded apps without an uploaded logo would otherwise render a broken-image
  // glyph — fall back to the Thanx mock asset. Paperclip never returns a blank
  // URL for a logo-less merchant; it returns its `.../missing.png` placeholder,
  // so we must treat that sentinel as "no logo" too (a plain truthiness check
  // would let the broken missing.png through).
  const brandedLogo = campaignMerchant?.logo_image_urls?.default
  const hasBrandedLogo =
    !!brandedLogo && !/\/missing\.\w+(\?|$)/.test(brandedLogo)
  const source =
    campaignMerchant?.branded_app && hasBrandedLogo
      ? brandedLogo
      : thanxLogoImage
  const appIcon = <img src={source} alt="app icon" className="app-icon" />

  const appName = campaignMerchant?.branded_app
    ? campaignMerchant?.nickname
    : 'Thanx'

  return (
    <div className="padding-huge padding-top-none">
      <div className="push-preview-toggles margin-bottom-small">
        <ButtonGroup>
          <Button
            bsSize="small"
            active={platform === 'ios'}
            onClick={() => setPlatform('ios')}
          >
            iOS
          </Button>
          <Button
            bsSize="small"
            active={platform === 'android'}
            onClick={() => setPlatform('android')}
          >
            Android
          </Button>
        </ButtonGroup>
      </div>
      <p className="text-center grey-50 font-size-12 margin-bottom-small">
        {t('hover_hint')}
      </p>
      <div
        className={`push-device push-device--${platform}`}
        style={{ backgroundImage: `url(${wallpaper})` }}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Status bar + clock are exported directly from Figma (SVG) so the
            device chrome matches the design exactly. */}
        <img
          className="push-device__statusbar"
          src={platform === 'ios' ? iosStatusBar : androidStatusBar}
          alt=""
          aria-hidden
        />
        {platform === 'ios' ? (
          <>
            <img
              className="push-device__ios-date"
              src={iosDate}
              alt=""
              aria-hidden
            />
            <img
              className="push-device__ios-time"
              src={iosTime}
              alt=""
              aria-hidden
            />
          </>
        ) : (
          <img
            className="push-device__android-time"
            src={androidTime}
            alt=""
            aria-hidden
          />
        )}

        <div className="push-device__notification">
          {platform === 'ios' ? (
            <IosPushPreview
              appIcon={appIcon}
              appName={appName || ''}
              title={title}
              subtitle={subtitle}
              body={pushText}
              imageUrl={imageUrl}
              isExpanded={isExpanded}
            />
          ) : (
            <AndroidPushPreview
              appIcon={appIcon}
              appName={appName || ''}
              title={title}
              body={pushText}
              imageUrl={imageUrl}
              isExpanded={isExpanded}
            />
          )}
        </div>

        <div className="push-device__home-indicator" />
      </div>
    </div>
  )
}

export default React.memo<Props>(PushRedesigned)
